/**
 * Admin Hub 権限定義（フロント — server/config/adminPermissions.js と同期）
 */

export const PERMISSION_IDS = [
  'clinics:read', 'clinics:write', 'clinics:delete',
  'users:read', 'users:write', 'users:delete',
  'roles:read', 'roles:write',
  'licenses:read', 'licenses:write',
  'products:read', 'products:write', 'products:delete',
  'health:read',
  'invitations:read', 'invitations:write',
  'announcements:read', 'announcements:write',
  'inquiries:read', 'inquiries:write',
  'email:read', 'email:write',
  'legal:read', 'legal:write',
  'homepage:read', 'homepage:write',
  'accounting:read', 'accounting:write', 'accounting:export',
  'logs:read', 'logs:export',
  'allowlist:read', 'allowlist:write',
  'settings:read', 'settings:write',
  'notifications:read', 'notifications:write',
];

export const HUB_ROLES = {
  SUPER_ADMIN: 'super_admin',
  OPERATOR: 'operator',
  VIEWER: 'viewer',
  ACCOUNTANT: 'accountant',
  CUSTOM: 'custom',
};

const READ_PERMISSION_IDS = PERMISSION_IDS.filter((id) => id.endsWith(':read'));

const OPERATOR_PERMISSIONS = [
  'clinics:read', 'clinics:write',
  'users:read', 'users:write',
  'products:read',
  'invitations:read', 'invitations:write',
  'announcements:read', 'announcements:write',
  'inquiries:read', 'inquiries:write',
  'email:read',
];

const ACCOUNTANT_PERMISSIONS = [
  'accounting:read',
  'accounting:write',
  'accounting:export',
];

export const PRESET_PERMISSIONS = {
  [HUB_ROLES.SUPER_ADMIN]: ['*'],
  [HUB_ROLES.OPERATOR]: OPERATOR_PERMISSIONS,
  [HUB_ROLES.VIEWER]: READ_PERMISSION_IDS,
  [HUB_ROLES.ACCOUNTANT]: ACCOUNTANT_PERMISSIONS,
};

export const PRESET_LABELS = {
  super_admin: '管理者',
  operator: 'SCXチーム',
  viewer: '閲覧のみ',
  accountant: '経理',
  custom: 'カスタム',
};

/** 権限 ID → 操作の日本語ラベル */
export const PERMISSION_LABELS = {
  'clinics:read': 'クリニックの閲覧',
  'clinics:write': 'クリニックの作成・編集',
  'clinics:delete': 'クリニックの削除',
  'users:read': 'ユーザーの閲覧',
  'users:write': 'ユーザーの作成・編集',
  'users:delete': 'ユーザーの削除',
  'roles:read': 'ロール設定の閲覧',
  'roles:write': 'ロール設定の編集',
  'licenses:read': 'ライセンスの閲覧',
  'licenses:write': 'ライセンスの編集',
  'products:read': 'プロダクトの閲覧',
  'products:write': 'プロダクトの作成・編集',
  'products:delete': 'プロダクトの削除',
  'health:read': 'ヘルスチェックの閲覧',
  'invitations:read': '招待特典の閲覧',
  'invitations:write': '招待特典の編集',
  'announcements:read': 'お知らせの閲覧',
  'announcements:write': 'お知らせの作成・編集',
  'inquiries:read': 'お問い合わせの閲覧',
  'inquiries:write': 'お問い合わせの対応・編集',
  'email:read': 'メール送信設定の閲覧',
  'email:write': 'メールのテスト送信',
  'legal:read': '規約改定の閲覧',
  'legal:write': '規約改定の編集',
  'homepage:read': 'HP管理の閲覧',
  'homepage:write': 'HP管理の編集・公開',
  'accounting:read': '会計の閲覧',
  'accounting:write': '会計レポートの生成・税設定',
  'accounting:export': '会計ファイルのエクスポート・Drive 送信',
  'logs:read': '監査ログの閲覧',
  'logs:export': '監査ログの CSV エクスポート',
  'allowlist:read': 'ログイン許可の閲覧',
  'allowlist:write': 'ログイン許可の追加・編集・削除',
  'settings:read': 'ハブ設定の閲覧',
  'settings:write': 'ハブ設定の編集',
  'notifications:read': '通知設定の閲覧',
  'notifications:write': '通知設定の編集',
};

export function resolvePresetPermissionIds(hubRole) {
  const preset = PRESET_PERMISSIONS[hubRole];
  if (!preset) return [];
  if (preset.includes('*')) return [...PERMISSION_IDS];
  return [...preset];
}

/** ロールごとの許可・制限操作一覧（編集 UI 用） */
export function getRoleCapabilitySummary(hubRole) {
  const allowedIds = resolvePresetPermissionIds(hubRole);
  const allowedSet = new Set(allowedIds);
  const toLabel = (id) => PERMISSION_LABELS[id] || id;

  return {
    allowed: allowedIds.map(toLabel),
    restricted: PERMISSION_IDS.filter((id) => !allowedSet.has(id)).map(toLabel),
  };
}

export const TAB_PERMISSIONS = {
  dashboard: ['clinics:read'],
  statistics: ['clinics:read'],
  clinics: ['clinics:read'],
  users: ['users:read'],
  licenses: ['licenses:read'],
  products: ['products:read'],
  'health-check': ['health:read'],
  roles: ['roles:read'],
  announcements: ['announcements:read'],
  inquiries: ['inquiries:read'],
  logs: ['logs:read'],
  'invitation-rewards': ['invitations:read'],
  legal: ['legal:read'],
  'hp-management': ['homepage:read'],
  accounting: ['accounting:read'],
  allowlist: ['allowlist:read'],
  settings: ['settings:read'],
  notifications: ['notifications:read'],
  'email-settings': ['email:read'],
  'dev-test': ['*'],
};

export function normalizePermissions(raw) {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.filter((p) => typeof p === 'string' && p.trim()).map((p) => p.trim()))];
}

export function can(user, permissionId) {
  if (!user || !permissionId) return false;
  const perms = normalizePermissions(user.permissions);
  if (perms.includes('*')) return true;
  return perms.includes(permissionId);
}

export function canAny(user, permissionIds) {
  if (!Array.isArray(permissionIds) || permissionIds.length === 0) return true;
  return permissionIds.some((id) => can(user, id));
}

export function canAccessTab(user, tabId) {
  const required = TAB_PERMISSIONS[tabId];
  if (!required) return true;
  return canAny(user, required);
}

/** ダッシュボード → 統計 → サイドバー順。開けるタブが無ければ null */
const TAB_ACCESS_ORDER = [
  'dashboard',
  'statistics',
  'clinics',
  'users',
  'roles',
  'health-check',
  'licenses',
  'products',
  'invitation-rewards',
  'accounting',
  'announcements',
  'inquiries',
  'email-settings',
  'legal',
  'hp-management',
  'logs',
  'allowlist',
  'settings',
  'notifications',
];

export function firstAccessibleTab(user) {
  return TAB_ACCESS_ORDER.find((tabId) => canAccessTab(user, tabId)) || null;
}

export function shouldLoadDashboardSummary(user) {
  return can(user, 'clinics:read');
}
